import { apiClient as api } from "./httpClient";

// Motor de oportunidades de agenda (RN-409): vagas por cancelamento × candidatos
// (realocação de feriado, dobradinha, antecipação). O aceite do MVP registra o
// resultado; a recepção reagenda no sistema operante e a reconciliação fecha a vaga.

export type OpportunityType = "holiday_relocation" | "double_session" | "anticipation" | "professional_cancellation";

export interface UrgentOpportunityItem {
  opportunityId: string;
  vacancyId: string;
  type: OpportunityType;
  score: number;
  vacancyStartsAt: string;
  professionalName: string | null;
  serviceName: string | null;
  patientName: string;
  candidateDate: string | null;
  hoursWithoutAction: number;
}

/** Reposição pendente no widget: a sessão perdida (profissional cancelou) a combinar */
export interface UrgentRepositionItem {
  needId: string;
  number: number;
  /** ISO local da entidade — a sessão que foi perdida */
  lostStartsAt: string;
  professionalName: string | null;
  serviceName: string | null;
  patientName: string | null;
  daysWaiting: number;
}

export interface OpportunitySummary {
  openVacancies: number;
  holidaySessions: number;
  revenueAtRisk: number;
  staleCount: number;
  urgent: UrgentOpportunityItem[];
  repositionsPending?: number;   // RN-409f: reposicoes em aberto
  /** as reposições mais antigas em aberto — itens de ação do widget (nomes mascarados) */
  urgentRepositions?: UrgentRepositionItem[];
}

export interface OpportunityCard {
  id: string;
  type: OpportunityType;
  score: number;
  status: "suggested" | "held" | "accepted" | "declined" | "superseded";
  holdExpiresAt: string | null;
  patientName: string;
  patientPhone: string | null;
  candidateDate: string | null;
  candidateProfessionalName: string | null;
  candidateServiceName: string | null;
  hoursWithoutAction: number;
  actedAt?: string | null;   // quando o resultado foi registrado (RN-409b)
  canReopen?: boolean;
  patientId: string;                 // abre o cadastro do paciente em nova aba
  inContactOnVacancyNumber?: number | null;  // reservado em outra vaga: nao contatar agora
  heldByName?: string | null;       // quem marcou "Vou contatar" NESTA oportunidade
  inContactByName?: string | null;  // quem marcou na outra vaga que segura o paciente
  /** RN-409i: sessão futura do candidato (antecipação/feriado) — o modal de edição a move */
  candidateScheduleId?: string | null;
}

// RN-409b: situacao pedida na fila; "open" mantem o comportamento historico
export type QueueSituation = "open" | "resolved" | "dismissed" | "all";

export interface VacancyCard {
  vacancyId: string;
  number: number;   // identificador legivel: OP-0000001
  status: "open" | "held" | "filled" | "dismissed";
  startsAt: string;
  endsAt: string;
  professionalName: string | null;
  serviceName: string | null;
  sourcePatientName: string | null;
  value: number | null;
  recommended: OpportunityCard | null;
  alternatives: OpportunityCard[];
  canReopen?: boolean;   // resolvida por decisao de alguem e ainda no futuro
  /** RN-409i: ids crus da vaga — pré-preenchem o modal de efetivação */
  professionalId?: string | null;
  serviceTypeId?: string | null;
}

export interface OpportunityQueue {
  openCount: number;
  cards: VacancyCard[];
  // janelas em vigor no motor, para a tela explicar o que esta sendo considerado
  vacancyWindowDays?: number;
  candidateHorizonDays?: number;
  vacancyWindowEnd?: string | null;
  candidateHorizonEnd?: string | null;
}

// RN-409f: reposicao quando o PROFISSIONAL cancela — o lado do paciente da vaga.
// Sugestoes calculadas na leitura pelo backend; nada e gravado.
export interface RepositionSuggestion {
  professionalId: string;
  professionalName: string;
  startsAt: string;
  endsAt: string;
}

export interface RepositionCard {
  id: string;
  number: number;   // identificador legivel: RP-0000001
  status: "open" | "held" | "resolved" | "dismissed" | "expired";
  lostStartsAt: string;
  lostEndsAt: string;
  patientName: string;
  patientPhone: string | null;
  patientId: string;
  professionalName: string | null;   // quem cancelou
  serviceName: string | null;
  value: number | null;
  holdExpiresAt: string | null;
  hoursWithoutAction: number;
  actedAt: string | null;
  heldByName: string | null;
  inContactOnVacancyNumber: number | null;   // contato exclusivo cruzado com a fila de vagas
  inContactByName: string | null;
  notes: string | null;
  sameTime: RepositionSuggestion[];           // mesmo horario, outro profissional que ja o atende
  sameProfessional: RepositionSuggestion[];   // outro dia com o MESMO profissional
  crossProfessionals: RepositionSuggestion[]; // outro dia com quem ja o atende
  /** RN-409i: ids crus do que foi perdido — pré-preenchem o modal de criação */
  professionalId?: string | null;
  serviceTypeId?: string | null;
  /** RN-409j: como a reposição foi fechada */
  resolutionKind?: RepositionResolutionKind | null;
}

export type RepositionResolutionKind = "rescheduled" | "detected" | "combined";

export const REPOSITION_RESOLUTION_LABELS: Record<RepositionResolutionKind, string> = {
  rescheduled: "registrada pelo reagendamento na agenda",
  detected: "detectada como sessão extra pela sincronização",
  combined: "combinada pela equipe",
};

export interface RepositionQueue {
  openCount: number;
  cards: RepositionCard[];
  suggestionHorizonDays?: number;
  suggestionHorizonEnd?: string | null;
}

export interface OpportunityTypeStat {
  type: OpportunityType;
  offered: number;
  accepted: number;
}

export interface WeeklyVacancyStat {
  weekLabel: string;
  opened: number;
  filled: number;
  /** perdas/recuperadas totais da semana (vagas + reposições) — visão única */
  lossesTotal?: number;
  recoveredTotal?: number;
}

/** Caminho de recuperação da visão única Perdas × Recuperações */
export interface RecoveryPathStat {
  path: string;
  recovered: number;
  denominator: number;
  rate: number;
}

export interface OpportunityReport {
  vacanciesLast30d: number;
  filledLast30d: number;
  reoccupationRate: number;
  medianHoursToFill: number | null;
  acceptedThisMonth: number;
  revenueRecoveredThisMonth: number;
  staleCount: number;
  byType: OpportunityTypeStat[];
  weekly: WeeklyVacancyStat[];
  // RN-409f: prestacao de contas das reposicoes
  repositionsCreated30d?: number;
  repositionsResolved30d?: number;
  repositionsDismissed30d?: number;
  repositionsPendingNow?: number;
  // Visão única Perdas × Recuperações (30d): vagas + reposições no mesmo funil
  lossesTotal30d?: number;
  recoveredTotal30d?: number;
  recoveryRate30d?: number;
  revenueRecovered30d?: number;
  paths?: RecoveryPathStat[];
}

export const opportunitiesService = {
  getSummary: async (entityId: string): Promise<OpportunitySummary> => {
    const response = await api.get(`/opportunities/summary/${entityId}`);
    return response.data;
  },

  getQueue: async (
    entityId: string,
    situation: QueueSituation = "open"
  ): Promise<OpportunityQueue> => {
    const response = await api.get(`/opportunities/queue/${entityId}`, {
      params: { situation: situation === "open" ? undefined : situation },
    });
    return response.data;
  },

  /** from/to (yyyy-MM-dd no relógio da entidade, inclusivos); ausentes = últimos 30 dias */
  getReport: async (entityId: string, from?: string, to?: string): Promise<OpportunityReport> => {
    const response = await api.get(`/opportunities/report/${entityId}`, { params: { from, to } });
    return response.data;
  },

  hold: async (opportunityId: string): Promise<string> => {
    const response = await api.post(`/opportunities/${opportunityId}/hold`);
    return response.data.holdExpiresAt;
  },

  getRepositions: async (
    entityId: string,
    situation: QueueSituation = "open"
  ): Promise<RepositionQueue> => {
    const response = await api.get(`/opportunities/repositions/${entityId}`, {
      params: { situation: situation === "open" ? undefined : situation },
    });
    return response.data;
  },

  holdReposition: async (repositionId: string): Promise<string> => {
    const response = await api.post(`/opportunities/repositions/${repositionId}/hold`);
    return response.data.holdExpiresAt;
  },

  // accepted=true: reposicao combinada (a recepcao efetiva na agenda; o sync confirma)
  // accepted=false: familia recusou — a necessidade e dispensada com o motivo
  resolveReposition: async (
    repositionId: string,
    accepted: boolean,
    notes?: string,
    // RN-409i: agendamento criado pelo modal que efetivou a reposição
    scheduleId?: string
  ): Promise<void> => {
    await api.post(`/opportunities/repositions/${repositionId}/resolve`,
      { accepted, notes, scheduleId });
  },

  dismissReposition: async (repositionId: string, reason?: string): Promise<void> => {
    await api.post(`/opportunities/repositions/${repositionId}/dismiss`, { reason });
  },

  resolve: async (
    opportunityId: string,
    accepted: boolean,
    channel?: string,
    notes?: string,
    // RN-409i: agendamento que efetivou o aceite pelo modal da agenda
    scheduleId?: string
  ): Promise<void> => {
    await api.post(`/opportunities/${opportunityId}/resolve`,
      { accepted, channel, notes, scheduleId });
  },

  dismissVacancy: async (vacancyId: string): Promise<void> => {
    await api.post(`/opportunities/vacancies/${vacancyId}/dismiss`);
  },

  // RN-409b: devolve a fila o que foi resolvido por engano; motivo obrigatorio
  reopenVacancy: async (vacancyId: string, reason: string): Promise<void> => {
    await api.post(`/opportunities/vacancies/${vacancyId}/reopen`, { reason });
  },

  reopenOpportunity: async (opportunityId: string, reason: string): Promise<void> => {
    await api.post(`/opportunities/${opportunityId}/reopen`, { reason });
  },

  // Planilha da fila respeitando os filtros da tela ("all" = sem filtro no backend)
  exportQueueXlsx: async (
    entityId: string,
    type?: string,
    professional?: string
  ): Promise<Blob> => {
    const response = await api.get(`/opportunities/queue/${entityId}/export`, {
      params: {
        type: type && type !== "all" ? type : undefined,
        professional:
          professional && professional !== "all" ? professional : undefined,
      },
      responseType: "blob",
    });
    return response.data;
  },

  exportReportXlsx: async (entityId: string, from?: string, to?: string): Promise<Blob> => {
    const response = await api.get(`/opportunities/report/${entityId}/export`, {
      params: { from, to },
      responseType: "blob",
    });
    return response.data;
  },
};

export const OPPORTUNITY_TYPE_LABELS: Record<OpportunityType, string> = {
  holiday_relocation: "Feriado",
  professional_cancellation: "Realocação",
  double_session: "Dobradinha",
  anticipation: "Antecipação",
};
